import React, { useState, useEffect } from 'react';
import { FaUserGraduate, FaSearch, FaFilter, FaDownload, FaPlus, FaEdit, FaTrash, FaCheckCircle } from 'react-icons/fa';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import api from '../../utils/api';
import LoadingSpinner from '../common/LoadingSpinner';
import Modal from '../common/Modal';

/**
 * Student Management component for school administrators
 * Allows managing students enrolled in the school
 */
const StudentManagement = () => {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  
  // State variables
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGrade, setFilterGrade] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [classes, setClasses] = useState([]);
  const [schoolId, setSchoolId] = useState(null);
  
  // Modal states
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  
  useEffect(() => {
    // Get school ID from user or URL
    const getSchoolId = async () => {
      try {
        if (user && user.school) {
          setSchoolId(user.school);
          return user.school;
        }
        
        // If admin is viewing a specific school
        if (user && user.role === 'system_admin') {
          // Could get from URL params if needed
          return null;
        }
      } catch (error) {
        console.error('Error getting school ID:', error);
        showError('Failed to determine school ID');
      }
    };
    
    const initData = async () => {
      const id = await getSchoolId();
      if (id) {
        await Promise.all([
          fetchStudents(id),
          fetchClasses(id)
        ]);
      }
    };
    
    initData();
  }, [user]);
  
  // Fetch students from the API
  const fetchStudents = async (id) => {
    try {
      setLoading(true);
      const response = await api.get(`/schools/${id}/students`);
      setStudents(response.data.data);
    } catch (error) {
      console.error('Error fetching students:', error);
      showError('Failed to fetch students');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch classes for filtering
  const fetchClasses = async (id) => {
    try {
      const response = await api.get(`/schools/${id}/classes`);
      setClasses(response.data.data);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };
  
  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    if (name === 'grade') {
      setFilterGrade(value);
    } else if (name === 'class') {
      setFilterClass(value);
    }
  };
  
  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setFilterGrade('');
    setFilterClass('');
  };
  
  // Export students data to CSV
  const exportStudents = () => {
    try {
      // Get filtered students
      const dataToExport = filteredStudents.map(student => ({
        'ID': student.admissionNumber || '',
        'First Name': student.firstName || '',
        'Last Name': student.lastName || '',
        'Grade': student.grade || '',
        'Class': student.class?.name || '',
        'Parent Name': `${student.parent?.firstName || ''} ${student.parent?.lastName || ''}`,
        'Parent Email': student.parent?.email || '',
        'Parent Phone': student.parent?.phoneNumber || ''
      }));
      
      // Convert to CSV
      const headers = Object.keys(dataToExport[0]);
      const csvContent = [
        headers.join(','),
        ...dataToExport.map(row => headers.map(header => `"${row[header]}"`).join(','))
      ].join('\n');
      
      // Create download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', 'students.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showSuccess('Students data exported successfully');
    } catch (error) {
      console.error('Error exporting students:', error);
      showError('Failed to export students data');
    }
  };
  
  // View student details
  const handleViewStudent = (student) => {
    setSelectedStudent(student);
    setShowViewModal(true);
  };
  
  // Filter students based on search and filters
  const filteredStudents = students.filter(student => {
    const matchesSearch = searchTerm === '' || 
      `${student.firstName} ${student.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (student.admissionNumber && student.admissionNumber.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesGrade = filterGrade === '' || student.grade === filterGrade;
    const matchesClass = filterClass === '' || (student.class && student.class._id === filterClass);
    
    return matchesSearch && matchesGrade && matchesClass;
  });
  
  // Get unique grades for filter dropdown
  const grades = [...new Set(students.map(student => student.grade).filter(Boolean))];
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-semibold text-gray-800 mb-1">Student Management</h1>
        <p className="text-gray-600">View and manage all students enrolled in your school</p>
      </div>
      
      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-soft p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search students..."
              className="form-input pl-10 py-2 w-full"
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="text-gray-400" />
            </div>
          </div>
          
          {/* Grade Filter */}
          <div>
            <select
              name="grade"
              value={filterGrade}
              onChange={handleFilterChange}
              className="form-select w-full"
            >
              <option value="">All Grades</option>
              {grades.map((grade, index) => (
                <option key={index} value={grade}>{grade}</option>
              ))}
            </select>
          </div>
          
          {/* Class Filter */}
          <div>
            <select
              name="class"
              value={filterClass}
              onChange={handleFilterChange}
              className="form-select w-full"
            >
              <option value="">All Classes</option>
              {classes.map((cls) => (
                <option key={cls._id} value={cls._id}>{cls.name}</option>
              ))}
            </select>
          </div>
          
          {/* Action Buttons */}
          <div className="flex space-x-2 items-center justify-end">
            <button
              onClick={clearFilters}
              className="btn-secondary-outline text-sm py-2"
              disabled={!searchTerm && !filterGrade && !filterClass}
            >
              Clear Filters
            </button>
            
            <button
              onClick={exportStudents}
              className="btn-secondary flex items-center text-sm"
              disabled={filteredStudents.length === 0}
            >
              <FaDownload className="mr-1" /> Export
            </button>
          </div>
        </div>
      </div>
      
      {/* Students List */}
      <div className="bg-white rounded-lg shadow-soft overflow-hidden">
        {loading ? (
          <div className="p-8 flex justify-center">
            <LoadingSpinner size="lg" />
          </div>
        ) : filteredStudents.length === 0 ? (
          <div className="p-8 text-center">
            <FaUserGraduate className="text-gray-300 w-16 h-16 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-800 mb-2">No Students Found</h3>
            <p className="text-gray-500">
              {searchTerm || filterGrade || filterClass ? 
                "No students match your search criteria. Try adjusting your filters." : 
                "There are no students registered in this school yet."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Class</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Parent</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredStudents.map((student) => (
                  <tr key={student._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center">
                          {student.profileImage ? (
                            <img 
                              src={student.profileImage} 
                              alt={`${student.firstName} ${student.lastName}`}
                              className="h-10 w-10 rounded-full object-cover"
                            />
                          ) : (
                            <span className="font-medium">
                              {student.firstName?.[0]}
                              {student.lastName?.[0]}
                            </span>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {student.firstName} {student.lastName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {student.gender}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{student.admissionNumber || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{student.grade || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{student.class?.name || 'Not Assigned'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {student.parent ? `${student.parent.firstName} ${student.parent.lastName}` : 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{student.parent?.email || 'N/A'}</div>
                      <div className="text-sm text-gray-500">{student.parent?.phoneNumber || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <button
                        onClick={() => handleViewStudent(student)}
                        className="text-primary-600 hover:text-primary-900 mr-3"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Student Details Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title="Student Details"
        size="lg"
      >
        {selectedStudent && (
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Personal Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm font-medium text-gray-500">First Name</div>
                      <div className="mt-1">{selectedStudent.firstName}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-500">Last Name</div>
                      <div className="mt-1">{selectedStudent.lastName}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-500">Gender</div>
                      <div className="mt-1">{selectedStudent.gender || 'Not specified'}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-500">Date of Birth</div>
                      <div className="mt-1">{selectedStudent.dateOfBirth ? new Date(selectedStudent.dateOfBirth).toLocaleDateString() : 'Not specified'}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-500">Admission Number</div>
                      <div className="mt-1">{selectedStudent.admissionNumber || 'Not assigned'}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-500">Grade</div>
                      <div className="mt-1">{selectedStudent.grade || 'Not specified'}</div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Academic Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm font-medium text-gray-500">Class</div>
                      <div className="mt-1">{selectedStudent.class?.name || 'Not assigned'}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-500">Enrollment Date</div>
                      <div className="mt-1">{selectedStudent.enrollmentDate ? new Date(selectedStudent.enrollmentDate).toLocaleDateString() : 'Not specified'}</div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Parent Information</h3>
                  {selectedStudent.parent ? (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm font-medium text-gray-500">Name</div>
                        <div className="mt-1">{selectedStudent.parent.firstName} {selectedStudent.parent.lastName}</div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-500">Email</div>
                        <div className="mt-1">{selectedStudent.parent.email}</div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-500">Phone</div>
                        <div className="mt-1">{selectedStudent.parent.phoneNumber || 'Not provided'}</div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-500">Relationship</div>
                        <div className="mt-1">{selectedStudent.relationship || 'Not specified'}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-gray-500">No parent information available</div>
                  )}
                </div>
                
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Information</h3>
                  <div className="text-sm text-gray-500">
                    {selectedStudent.additionalInfo || 'No additional information provided'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default StudentManagement; 